// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: MIT-0

import * as faceapi from "face-api.js";
import Logger from "js-logger";
import { ChallengeDetails } from "@/js/RemoteStarter";
import { DrawOptions } from "@/js/Drawer";
import { FaceState } from "@/js/States";
import { FailState } from "@/js/States";
import { NoseState } from "@/js/States";
import { State } from "@/js/States";
import { StateOutput } from "@/js/States";
import { SuccessState } from "@/js/States";
import { EventLogger } from "@/js/EventLogger";
import { AreaState, FaceUpState } from "./States";

export interface StateManagerOutput {
  readonly end: boolean;
  readonly success?: boolean;
  readonly shouldSaveFrame: boolean;
  readonly drawOptions?: DrawOptions;
  readonly helpMessage?: string;
  readonly helpAnimationNumber?: number;
}

export class StateManager {
  private readonly challengeDetails: ChallengeDetails;

  private currentState!: State;
  private endTime!: number;

  constructor(challengeDetails: ChallengeDetails) {
    this.challengeDetails = challengeDetails;
    this.changeCurrentState(new FaceState(this.challengeDetails));
  }

  process(
    result: faceapi.WithFaceLandmarks<{ detection: faceapi.FaceDetection }, faceapi.FaceLandmarks68>[]
  ): StateManagerOutput {
    Logger.debug(`current state: ${this.currentState.getName()}`);

    if (this.endTime > 0 && Date.now() / 1000 > this.endTime) {
      Logger.warn(`fail: state ${this.currentState.getName()} timed out`);
      this.changeCurrentState(new FailState(this.challengeDetails));
    }
    const stateOutput: StateOutput = this.currentState.process(result);
    if (stateOutput.nextState) {
      this.changeCurrentState(stateOutput.nextState);
    }

    let end = false;
    let shouldSaveFrame = false;
    let success;
    if (this.currentState.getName() === SuccessState.NAME) {
      end = true;
      success = true;
      shouldSaveFrame = true;
    } else if (this.currentState.getName() === FailState.NAME) {
      end = true;
      success = false;
    } else if (this.currentState.getName() === NoseState.NAME) {
      shouldSaveFrame = true;
    } else if (this.currentState.getName() === FaceUpState.NAME) {
      shouldSaveFrame = true;
    }
    return {
      end: end,
      success: success,
      shouldSaveFrame: shouldSaveFrame,
      drawOptions: stateOutput.drawOptions,
      helpMessage: stateOutput.helpMessage,
      helpAnimationNumber: stateOutput.helpAnimationNumber
    };
  }

  private changeCurrentState(state: State) {
    if (this.currentState !== state) {
      this.currentState = state;
      this.endTime =
        state.getMaximumDurationInSeconds() != -1 ? Date.now() / 1000 + state.getMaximumDurationInSeconds() : -1;
      this.sendStateChangedEvent();
    }
  }
  private sendStateChangedEvent() {
    const stateName = this.currentState.getName();
    switch (stateName) {
      case FaceState.NAME:
        EventLogger.singleton.sendStartFaceStateEvent();
        break;
      case AreaState.NAME:
        EventLogger.singleton.sendStartAreaStateEvent();
        break;
      case NoseState.NAME:
        EventLogger.singleton.sendStartNoseStateEvent();
        break;
      case FaceUpState.NAME:
        EventLogger.singleton.sendStartFaceUpStateEvent();
        break;
    }
  }
}
